// Eager slim slice of the calendar function barrel (#16 calendar slim).
//
// The full `./index.js` barrel re-exports the whole calendar function surface —
// which is exactly two source files: calendarUtils.js (date/lane/timezone/
// recurrence helpers + the freshBooking factory) and saveEvent.js
// (the event write ops). The unified shell used to drag both into its EAGER
// chunk by folding the calendar context via `sharedLibraries.js`.
//
// Calendar is now a lazy sub-app: the `/calendar` route folds the FULL calendar
// context (components + functions + state) into the live shell context on first
// mount (workspace/pages/index.js → lazySharedLibPage). So the eager chunk only
// needs the calendar functions that get el.call'd (or statically imported) from
// OUTSIDE a calendar-mounted component — i.e. on a surface that never folds the
// calendar context.
//
// An exhaustive grep of the whole eager surface (workspace app.js/pages/
// components/functions, shared/**, chat, meet, canvas, and the other lazy
// sub-apps' eager contexts) for cross-route calendar-function callers found:
//   • saveEvent — el.call'd by chat's MessageList (send-a-message-as-
//     event, /chat), meet (meetUtils.js `applyFn` + applySuggestion, /meet), the
//     workspace AI layer (functions/aiAssistant.js `create_event`) on ANY route,
//     AND the eager CalEventDetailModal (join-call/move) + CalNewBookingModal
//     (save/move).
//   • deleteEvent / updateSharedEventFields — el.call'd by the eager
//     CalEventDetailModal (delete / this-vs-series edit) + CalNewBookingModal.
//   • freshBooking — el.call'd by the eager modals' edit/new paths AND by
//     workspace/functions/newBookingModal.js's `openNewBookingModal` (the
//     shell-root opener CalNewBookingModal is triggered through, fires on
//     ANY route — see components/eagerSlice.js). meet/functions/
//     openSuggestionModal.js used to import it directly; it now routes
//     through `openNewBookingModal` too, so calendarUtils.js's eager
//     registration is what keeps that el.call resolving.
// Every OTHER calendar util (getWeekDays, assignLanes, getMonthDays, getHours,
// pxToTime, eventToTop, getUnavailableRanges, …) is only el.call'd from a
// calendar-mounted grid (WeekView/MonthView/TeamView/TeamNowView/MiniCalendar),
// i.e. AFTER a fold registers it — so it rides the lazy chunk. But it costs
// nothing to register the whole calendarUtils surface eagerly: calendarUtils.js
// is ALREADY eager (statically imported by all three eager modals), so this
// slice adds no byte weight for it and guarantees no el.call('<calendarUtil>')
// from any surface ever misses.
//
// Byte note: saveEvent.js is the only net-new eager module (~9KB) — the
// price of keeping chat/meet/AI event writes + the modal save/delete/move working
// from any route. Both function files import only shell/shared helpers
// (roleHelpers, sdk, config, coreHeaders — verified: no function file imports a
// component file), so this slice can never drag a heavy calendar component into
// the eager chunk.
//
// calendar/package.json has no `sideEffects: false`, so re-exporting through
// `./index.js` would NOT tree-shake. The two source files are `export *`-ed
// directly below (the calendar function surface is exactly these two files, and
// both carry eager-referenced functions), keeping the eager set explicit and
// future-proof against a new /calendar-only function file being added to index.js.
export * from './calendarUtils.js'
export {
  deleteEvent,
  saveEvent,
  setEventRsvp,
  triggerCalendarSync,
  updateSharedEventFields
} from './saveEvent.js'
// Availability grid ↔ AvailabilityRule persistence + summary helpers. Eager
// because the eager CalProfileModal (openProfileModal fires on ANY route) and
// the shell's MemberProfile/ProfileAvailabilityGrid (/my + /profile, which
// never fold the calendar context) el.call these. Both files import only
// shared helpers (sdk, peopleUtils) — no component weight rides along.
export * from './availabilityShape.js'
export * from './availabilityUtils.js'
// The AI-answer view provider (calendarAiViews) and its data helpers: the
// shell reads the provider on ANY route, before the calendar context folds
// (workspace functions/assistantAppViews.js).
export * from './calendarAgenda.js'
// The AI-answer FORM provider (calendarAiForms) and its opener — read on ANY
// route (workspace functions/assistantAppForms.js).
export * from './calendarAiForm.js'
// The delete dialog and the recurrence helpers the EAGER modals el.call.
// CalEventDetailModal and CalNewBookingModal both fire from shell-root
// openers on routes that never fold the calendar context (see
// components/eagerSlice.js), so these have to be registered here or the
// delete button and the repeat fields resolve to `undefined` off /calendar.
// Neither file carries component weight: confirmEventDeletion.js imports
// nothing, recurrence.js imports nothing.
export * from './confirmEventDeletion.js'
// The proposal Accept / Decline and their gates — the eager
// CalEventDetailModal el.calls them from its proposal banner on any route.
// It imports nothing either.
export * from './eventProposal.js'
// By name, not `export *` — calendarUtils.js above already re-exports
// `recurrenceAllowsDate`, and two star paths for one name make it ambiguous
// and silently drop it (same reason functions/index.js names these five).
export {
  DEFAULT_RECURRENCE_DRAFT,
  normalizeOccurrenceCount,
  recurrenceFieldsFromMetadata,
  recurrenceMetadataForEvent,
  recurrencePayloadFromBooking
} from './recurrence.js'
